// Publisher-side version-visibility / retention wiring (szzt-spec.md §16, §17.6; roadmap item 16).
//
// §16 keeps ALL versions until the publisher explicitly prunes; §17.6 says a per-site setting MAY
// expose "version-visibility and retention controls to publishers" (the gateway already serves only
// `site.current`, so a historical version's "visibility" is exactly whether its records still exist —
// prune is that control). Roadmap item 9 fixed the shape: NO scheduled auto-pruning; the real feature
// is an EXPLICIT, opt-in publisher action ("delete these old versions"). This module is the thin,
// UNIT-TESTED seam the publisher UI drives — it adds NO selection logic of its own, delegating wholly
// to the tested `@szzt/publish` primitives (`planRetention` for the live preview, `planRetentionPrune`
// for the executable delete plan). The DOM/PDS glue that resolves records and runs the prune stays in
// `main.ts` (typecheck-only); the load-bearing "which versions" decision lives here so it is testable.

import {
  planRetention,
  planRetentionPrune,
  type PrunePlan,
  type RetentionPlan,
} from "@szzt/publish";

/**
 * One site version as the retention panel sees it — a `app.szzt.deployment` plus the fields the
 * keep-last-N primitives order and delete by. `manifestUri` is the deployment's `tile.uri` (the paired
 * `ing.dasl.masl` record deleted alongside it); `blobCids` are that manifest's resource CIDs, present
 * only when the glue has resolved them (they affect only the informational releasable-blob report,
 * never the deletes). `createdAt` may be undefined for a record that carried none — passed through as
 * an unparseable date so the primitive's fail-safe retains it rather than dating it wrong.
 */
export interface RetentionVersion {
  /** AT-URI of the `app.szzt.deployment` record — this version's identity. */
  deploymentUri: string;
  /** AT-URI of the paired `ing.dasl.masl` manifest record (deployment `tile.uri`). */
  manifestUri: string;
  /** ISO `createdAt`, or undefined when the record carried none (retained fail-safe). */
  createdAt: string | undefined;
  /** Resource blob CIDs this version's manifest references, when resolved (releasable-blob report only). */
  blobCids?: string[];
}

/**
 * Coerce a raw keep-count input to the non-negative integer `planRetention` requires, so a stray
 * value from the UI never throws `RangeError` mid-preview. Non-finite or negative → 0 (keep only the
 * current version); a fractional value floors.
 */
export function clampKeep(raw: number): number {
  if (!Number.isFinite(raw) || raw < 0) return 0;
  return Math.floor(raw);
}

/**
 * Preview a keep-last-N policy over a version list: which versions it would prune vs retain. Cheap (no
 * manifest resolution — reads only `deploymentUri` + `createdAt`), so the panel recomputes it on every
 * keystroke to show a live "will delete N" summary. Delegates the whole selection to `planRetention`;
 * the current version is never selected, and an undatable version is retained (fail-safe).
 */
export function previewKeepLast(
  versions: readonly RetentionVersion[],
  keep: number,
  currentDeploymentUri: string,
): RetentionPlan {
  return planRetention({
    currentDeploymentUri,
    deployments: versions.map((v) => ({
      deploymentUri: v.deploymentUri,
      createdAt: v.createdAt ?? "", // undatable → Date.parse NaN → retained (planRetention fail-safe)
    })),
    keep,
  });
}

/**
 * Build the EXECUTABLE prune plan for a keep-last-N policy — the atomic delete writes plus the
 * releasable-blob report the `prune()` executor runs. Delegates wholly to `planRetentionPrune`; the UI
 * contributes no retention logic. A version whose `blobCids` are unresolved passes an empty list, which
 * only empties that version's contribution to `releasableBlobs` — the load-bearing deletes are
 * unaffected. The current version is never in the plan (the primitive refuses it).
 */
export function planKeepLastPrune(
  versions: readonly RetentionVersion[],
  keep: number,
  currentDeploymentUri: string,
): PrunePlan {
  return planRetentionPrune({
    currentDeploymentUri,
    deployments: versions.map((v) => ({
      deploymentUri: v.deploymentUri,
      manifestUri: v.manifestUri,
      createdAt: v.createdAt ?? "",
      blobCids: v.blobCids ?? [],
    })),
    keep,
  });
}

/**
 * One-line human summary of what applying a keep-last-N preview will do — drives the panel's hint and
 * confirm text. Derived purely from a {@link RetentionPlan}'s counts.
 */
export function retentionSummary(plan: RetentionPlan): string {
  const n = plan.prune.length;
  if (n === 0) return "No older versions to delete at this keep count.";
  return `Delete ${n} older version${n === 1 ? "" : "s"}, keeping ${plan.retained.length}.`;
}
